import { useQuery } from "@tanstack/react-query";
import { PageHead } from "@/components/PageHead";

import { link } from "@/api/client";
import { fmtBytes, fmtCount, POLL } from "@/lib/format";
import { Chip, Value } from "@/components/Chip";


/** Application traffic vs protocol overhead — NEVER blurred (the msg/s bug, and
 *  the "365 messages" that were really packets, both came from blurring them). */
export function StatisticsPage() {
  const { data } = useQuery({ queryKey: ["link", "stats"], queryFn: link.stats, refetchInterval: POLL.STATE });
  const m = data?.metrics;
  const ing = data?.ingest;
  const kind = data?.broker.kind ?? "—";

  return (
    <div className="mpage on">
      <PageHead eyebrow="Link · Statistics" title="Broker &amp; ingest health" />

      <div className="section-label">Application traffic — PUBLISHes (what telemetry means)</div>
      <div className="livegrid" style={{ marginBottom: 16 }}>
        <div>
          <div className="k">Rate in</div>
          <div className="v num">
            <Value v={m?.msgs_per_min_in != null ? m.msgs_per_min_in.toFixed(1) : null} />
          </div>
          <div className="s">
            msg/min · = <Value v={m?.msgs_per_sec_in != null ? m.msgs_per_sec_in.toFixed(2) : null} />{" "}
            msg/s
          </div>
        </div>
        <div>
          <div className="k">Publishes in</div>
          <div className="v num" title={m?.publish_in?.toLocaleString("en-IN")}>
            {fmtCount(m?.publish_in)}
          </div>
          <div className="s">since broker start</div>
        </div>
        <div>
          <div className="k">Deliveries out</div>
          <div className="v num" title={m?.publish_out?.toLocaleString("en-IN")}>
            {fmtCount(m?.publish_out)}
          </div>
          <div className="s">to subscribers</div>
        </div>
        <div>
          <div className="k">Payload in</div>
          <div className="v num" title={`${m?.publish_bytes_in ?? "—"} B`}>
            {fmtBytes(m?.publish_bytes_in)}
          </div>
          <div className="s">PUBLISH payloads</div>
        </div>
        <div>
          <div className="k">Dropped</div>
          <div className="v num">
            <Value v={m?.dropped ?? null} />
          </div>
          <div className="s">publish drops</div>
        </div>
      </div>

      <div className="section-label">Protocol &amp; broker state — packets, not payloads</div>
      <div className="livegrid" style={{ marginBottom: 16 }}>
        <div>
          <div className="k">Packets in / out</div>
          <div className="v num"
            title={m ? `${m.packets_in?.toLocaleString("en-IN")} / ${m.packets_out?.toLocaleString("en-IN")}` : undefined}>
            {fmtCount(m?.packets_in)} / {fmtCount(m?.packets_out)}
          </div>
          <div className="s">incl. keepalives, acks</div>
        </div>
        <div>
          <div className="k">Wire in / out</div>
          <div className="v num"
            title={m ? `${m.bytes_in ?? "—"} / ${m.bytes_out ?? "—"} bytes` : undefined}>
            {m ? `${fmtBytes(m.bytes_in)} / ${fmtBytes(m.bytes_out)}` : "—"}
          </div>
          <div className="s">all MQTT traffic</div>
        </div>
        <div>
          <div className="k">Subscriptions</div>
          <div className="v num">
            <Value v={m?.subscriptions ?? null} />
          </div>
          <div className="s">active</div>
        </div>
        <div>
          <div className="k">Retained msgs</div>
          <div className="v num">
            <Value v={m?.retained ?? null} />
          </div>
          <div className="s">stored</div>
        </div>
        <div>
          <div className="k">Broker uptime</div>
          <div className="v num">
            <Value v={m?.uptime_s != null ? `${Math.floor(m.uptime_s / 60)} min` : null} />
          </div>
          <div className="s">&nbsp;</div>
        </div>
      </div>

      <div style={{ fontSize: 10.5, color: "var(--t-dim)", marginBottom: 8 }}>
        Source: $SYS topics · broker = {kind}
        {kind === "mosquitto" &&
          " (bench — EMQX adds per-client stats, auth/ACL counters, latency percentiles)"}
      </div>

      <div className="section-label">
        Ingest pipeline{" "}
        {!ing ? (
          <Chip tone="mute">NO DATA — worker has not reported yet</Chip>
        ) : ing.age_s > 30 ? (
          <Chip tone="bad">STALE — worker silent {Math.round(ing.age_s)}s</Chip>
        ) : (
          <Chip tone="ok">LIVE</Chip>
        )}{" "}
        {ing && (ing.gate_open === false
          ? <Chip tone="warn">GATE CLOSED — fleet holding</Chip>
          : <Chip tone="ok">GATE OPEN</Chip>)}
      </div>
      <div className="panel panel-pad" style={{ paddingTop: 8, paddingBottom: 8 }}>
        {([
          ["Rows written to Vault · hydrated, complete", ing?.stats.rows_written],
          ["Latest-state upserts · ts-guarded", ing?.stats.latest_upserts],
          ["Stale-latest skipped · guard held newer state", ing?.stats.latest_stale_skipped],
          ["Duplicate drops · drain replay, idempotent", ing?.stats.duplicates],
          ["Hydration parked · seq gap, awaiting snapshot", ing?.stats.parked],
          ["Dead-lettered · parse fail / wrong catalog", ing?.stats.deadlettered],
          ["Stale-deployment dropped · refurb guard (D-35)", ing?.stats.stale_dropped],
        ] as [string, number | undefined][]).map(([label, v]) => (
          <div className="rec" key={label}>
            <span className="what">{label}</span>
            <b className="num" title={v?.toLocaleString("en-IN")}>{v != null ? fmtCount(v) : "—"}</b>
          </div>
        ))}
        {ing && (
          <div style={{ fontSize: 10.5, color: "var(--t-dim)", marginTop: 6 }}>
            counters since worker start ({new Date(ing.started_at).toLocaleString()}) ·
            reported {ing.age_s}s ago
          </div>
        )}
      </div>
    </div>
  );
}
